import type { Price } from '@/root/shared-types/dto/SailPricing'
import type { SailPackage } from '@/root/shared-types/reservation/reservation-types'
import { getPriceCategoryAttribute } from '@/root/modules/shared/SelectTicketPage/helpers/priceCategoryAttribute/priceCategoryAttribute'

export const ALTERNATIVE_PRICE_CATEGORY_FOR_RESIDENT = 'ALTERNATIVE_PRICE_CATEGORY_FOR_RESIDENT' as const

export interface ResidentOverrideCategory {
  code: string
  label: string
}

/**
 * The resident category a route sells as an opt-in on top of the one the register hands out —
 * the person accompanying a child or a person with a profound disability.
 *
 * The route names it in `ALTERNATIVE_PRICE_CATEGORY_FOR_RESIDENT`, which is what gates the option:
 * a route without the attribute does not sell it. The category itself words the checkbox in
 * `DESCRIPTION_<LOCALE>`.
 */
export const getResidentOverrideCategory = (
  prices: Price[] | undefined,
  locale: string,
  sailPackage?: Pick<SailPackage, 'routeAttributes'>
): ResidentOverrideCategory | undefined => {
  const code = sailPackage?.routeAttributes?.[ALTERNATIVE_PRICE_CATEGORY_FOR_RESIDENT]
  if (typeof code !== 'string' || code === '') return undefined

  const priceCategory = prices?.find((price) => price.priceCategory.code === code)?.priceCategory

  if (!priceCategory) return undefined

  const description =
    getPriceCategoryAttribute(priceCategory, `DESCRIPTION_${locale.toUpperCase()}`) ??
    getPriceCategoryAttribute(priceCategory, 'DESCRIPTION_EN')

  return {
    code: priceCategory.code,
    label: typeof description === 'string' ? description : (priceCategory.titles[locale] ?? priceCategory.title),
  }
}
